import React, { useState, useEffect } from 'react'
import { TrendingUp, ArrowUpRight, MoreHorizontal, Download, Filter, ChevronDown } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const Home = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState(null)
  const [monthlyData, setMonthlyData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, monthlyRes] = await Promise.allSettled([
          api.get('/dashboard/stats'),
          api.get('/dashboard/monthly-stats')
        ])
        if (statsRes.status === 'fulfilled' && statsRes.value.data?.success) setStats(statsRes.value.data.data)
        if (monthlyRes.status === 'fulfilled' && monthlyRes.value.data?.success) setMonthlyData(monthlyRes.value.data.data || [])
      } catch (error) {
        console.error('Failed to fetch:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E4D40]" /></div>
  }

  const totalScans = stats?.totalScans || 0
  const authenticCount = stats?.authenticProducts || 0
  const counterfeitCount = stats?.counterfeitDetected || 0
  const accuracyRate = stats?.accuracyRate || 0

  const chartData = [...(monthlyData || [])].reverse().slice(0, 12).map(m => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    const monthIdx = parseInt(m.month?.substring(5)) - 1
    return {
      month: monthNames[monthIdx] || m.month?.substring(5) || '',
      authentic: m.authentic || 0,
      counterfeit: m.counterfeit || 0,
      total: m.total || 0
    }
  })

  const satisfactionScore = Math.round(accuracyRate * 2.5)

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Verification Overview</h1>
          <p className="text-gray-500 mt-1">Analyze authentication trends to make data-driven decisions</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm">
            Monthly <ChevronDown className="h-4 w-4 ml-2" />
          </button>
          <button className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm">
            <Download className="h-4 w-4 mr-2" /> Export
          </button>
          <button className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-[#1E4D40] rounded-xl hover:bg-[#163a30] shadow-sm">
            <Filter className="h-4 w-4 mr-2" /> Filter
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-[#A3E635] to-[#84cc16] rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute top-4 right-4"><ArrowUpRight className="h-5 w-5 text-white/70" /></div>
          <p className="text-sm font-medium text-green-900/70 mb-1">Total Scans</p>
          <p className="text-4xl font-bold text-green-900">{totalScans.toLocaleString()}</p>
          <div className="mt-4 inline-flex items-center px-2.5 py-1 bg-white/30 rounded-full text-xs font-medium text-green-900">
            <TrendingUp className="h-3 w-3 mr-1" /> +12% Than last month
          </div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
          <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/5 rounded-full" />
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-sm relative overflow-hidden">
          <div className="absolute top-4 right-4"><ArrowUpRight className="h-5 w-5 text-gray-300" /></div>
          <p className="text-sm font-medium text-gray-500 mb-1">Authentic Products</p>
          <p className="text-4xl font-bold text-gray-900">{authenticCount.toLocaleString()}</p>
          <div className="mt-4 inline-flex items-center px-2.5 py-1 bg-green-50 rounded-full text-xs font-medium text-green-700">
            <TrendingUp className="h-3 w-3 mr-1" /> +{Math.round(accuracyRate/10)}% Than last month
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Detection Accuracy</p>
            <MoreHorizontal className="h-5 w-5 text-gray-300" />
          </div>
          <p className="text-4xl font-bold text-gray-900">{accuracyRate}%</p>
          <div className="mt-4 w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#1E4D40] to-[#A3E635]" style={{ width: `${accuracyRate}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span className="flex items-center"><span className="w-2 h-2 bg-[#1E4D40] rounded-full mr-1" />Authentic</span>
            <span className="flex items-center"><span className="w-2 h-2 bg-[#A3E635] rounded-full mr-1" />Target</span>
            <span className="flex items-center"><span className="w-2 h-2 bg-gray-200 rounded-full mr-1" />Remaining</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Verification Score</h3>
              <p className="text-sm text-gray-500">Top Detection Accuracy</p>
            </div>
            <MoreHorizontal className="h-5 w-5 text-gray-300" />
          </div>
          <div className="flex justify-center py-6">
            <div className="relative w-48 h-24 overflow-hidden">
              <svg viewBox="0 0 200 100" className="w-full h-full">
                <path d="M 20 90 A 80 80 0 0 1 180 90" fill="none" stroke="#e5e7eb" strokeWidth="16" strokeLinecap="round" />
                <path d="M 20 90 A 80 80 0 0 1 180 90" fill="none" stroke="url(#gaugeGrad)" strokeWidth="16" strokeLinecap="round"
                  strokeDasharray={`${(accuracyRate / 100) * 251.2} 251.2`} />
                <defs>
                  <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#1E4D40" />
                    <stop offset="50%" stopColor="#16a34a" />
                    <stop offset="100%" stopColor="#A3E635" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                <p className="text-3xl font-bold text-gray-900">{satisfactionScore}</p>
                <p className="text-xs text-gray-500">Scans this month</p>
              </div>
            </div>
          </div>
          <div className="flex justify-center space-x-4 mt-2 text-xs">
            <span className="flex items-center"><span className="w-2 h-2 bg-[#1E4D40] rounded-full mr-1" />High</span>
            <span className="flex items-center"><span className="w-2 h-2 bg-[#A3E635] rounded-full mr-1" />Low</span>
          </div>
          <div className="mt-4 bg-green-50 rounded-xl p-3 text-center">
            <span className="inline-flex items-center text-xs font-medium text-green-700">
              <TrendingUp className="h-3 w-3 mr-1" /> +{Math.round(accuracyRate/8)}% Verification Score: {(accuracyRate/20).toFixed(1)}/5
            </span>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">Exceptional detection and quick responses</p>
        </div>

        <div className="lg:col-span-3 bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Statistics</h3>
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                Verification Rate <ChevronDown className="inline h-3 w-3 ml-1" />
              </button>
              <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                Summary <ChevronDown className="inline h-3 w-3 ml-1" />
              </button>
              <button className="px-3 py-1.5 text-xs font-medium text-white bg-[#1E4D40] rounded-lg">
                Yearly <ChevronDown className="inline h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                formatter={(value, name) => [value.toLocaleString(), name === 'authentic' ? 'Authentic' : 'Flagged']}
              />
              <Bar dataKey="authentic" fill="#A3E635" radius={[6, 6, 0, 0]} name="authentic" />
              <Bar dataKey="counterfeit" fill="#1E4D40" radius={[6, 6, 0, 0]} name="counterfeit" opacity={0.6} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {stats?.recentScans && stats.recentScans.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-gray-500">Live</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentScans.slice(0, 6).map((scan, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{scan.product_name || scan.brand || 'Product'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                        scan.scan_result === 'authentic' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{scan.scan_result === 'authentic' ? 'Authentic' : 'Flagged'}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{scan.location_city || 'Unknown'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{scan.scanned_at ? new Date(scan.scanned_at).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
